import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CategorizedFacility, getCategoryIcon, POPULAR_FACILITIES } from "../constants";

const normalize = (value: string) => value.trim().toLowerCase();

const MAX_NUMBER_FACILITIES = 10;

const POPULARITY_RANK = new Map(POPULAR_FACILITIES.map((facility, index) => [normalize(facility), index]));

const CATEGORY_ICON_COLORS: Record<string, string> = {
  Accessibility: "text-icon-amenity-accessibility",
  "Banking & Payments": "text-icon-amenity-banking-payments",
  "Business & Events": "text-icon-amenity-business-events",
  Dining: "text-icon-amenity-dining",
  "Family & Kids": "text-icon-amenity-family-kids",
  "Front Desk": "text-icon-amenity-front-desk",
  Housekeeping: "text-icon-amenity-housekeeping",
  "In-Room Services": "text-icon-amenity-in-room-services",
  "Internet Access": "text-icon-amenity-internet-access",
  Pets: "text-icon-amenity-pets",
  "Safety & Security": "text-icon-amenity-safety-security",
  "Shopping Centres": "text-icon-amenity-shopping-centres",
  "Smoking Policy": "text-icon-amenity-smoking-policy",
  "Transport & Parking": "text-icon-amenity-transport-parking",
  "Travel Sustainable": "text-icon-amenity-travel-sustainable",
  "Wellness & Recreation": "text-icon-amenity-wellness-recreation",
};
const DEFAULT_ICON_COLOR = "text-icon-active";

const getFacilityIconColor = (category: string) => CATEGORY_ICON_COLORS[category] ?? DEFAULT_ICON_COLOR;

const getPopularFacilities = (categorizedFacilities: CategorizedFacility[]) => {
  const byRank: Array<{ name: string; category: string }> = [];
  for (const { category, items } of categorizedFacilities) {
    for (const name of items) {
      const rank = POPULARITY_RANK.get(normalize(name));
      if (rank !== undefined) byRank[rank] = { name, category };
    }
  }
  return byRank.filter(Boolean).slice(0, MAX_NUMBER_FACILITIES);
};

const PopularAmenities: FC<{ categorizedFacilities: CategorizedFacility[] }> = ({ categorizedFacilities }) => {
  const { t } = useTranslation();
  const popularFacilities = useMemo(() => getPopularFacilities(categorizedFacilities), [categorizedFacilities]);

  if (!popularFacilities.length) return null;

  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-font-default">{t("Hotels.booking.popularFacilities")}</h3>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {popularFacilities.map(({ name, category }) => {
          const FacilityIcon = getCategoryIcon(category);
          return (
            <div key={name} className="flex items-center gap-1.5 text-sm text-font-subtle">
              <FacilityIcon className={`h-4 w-4 shrink-0 ${getFacilityIconColor(category)}`} />
              <span>{name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PopularAmenities;
